import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getChatbots, createChatbot, deleteChatbot } from '../api';
import type { ChatbotListItem } from '@neural-intelligence/shared';
import AppHeader from '../components/AppHeader';
import HeaderUtilityActions from '../components/HeaderUtilityActions';

export default function ChatbotList() {
  const { user } = useAuth();
  const { toast, confirm } = useToast();
  const navigate = useNavigate();
  const [chatbots, setChatbots] = useState<ChatbotListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'date'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadChatbots();
  }, []);

  async function loadChatbots() {
    try {
      const result = await getChatbots();
      if (result.success && result.data) {
        setChatbots(result.data);
      }
    } catch (e) {
      // Error loading - UI will show empty state
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;

    setCreating(true);
    try {
      const result = await createChatbot({ name: newName.trim() });
      if (result.success && result.data) {
        navigate(`/chatbots/${result.data.id}`);
      }
    } catch (e) {
      toast.error('Failed to create chatbot. Please try again.');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    const confirmed = await confirm({
      title: 'Delete Chatbot',
      description: 'Are you sure you want to permanently delete this chatbot? This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!confirmed) return;

    setDeletingId(id);
    try {
      await deleteChatbot(id);
      setChatbots(chatbots.filter((c) => c.id !== id));
    } catch (e) {
      toast.error('Failed to delete chatbot. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }

  // Filter and sort chatbots
  const filteredAndSortedChatbots = useMemo(() => chatbots
    .filter(chatbot =>
      chatbot.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name') {
        const comparison = a.name.localeCompare(b.name);
        return sortOrder === 'asc' ? comparison : -comparison;
      } else {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      }
    }), [chatbots, searchQuery, sortBy, sortOrder]);

  const categorizedChatbots = useMemo(() => {
    const salesEditable: ChatbotListItem[] = [];
    const deployed: ChatbotListItem[] = [];
    const testing: ChatbotListItem[] = [];

    for (const chatbot of filteredAndSortedChatbots) {
      if (chatbot.is_testing) {
        testing.push(chatbot);
      } else if (chatbot.editable_by_sales) {
        salesEditable.push(chatbot);
      } else {
        deployed.push(chatbot);
      }
    }

    return [
      { key: 'sales', label: 'Sales Editable', items: salesEditable },
      { key: 'deployed', label: 'Deployed', items: deployed },
      { key: 'testing', label: 'Testing', items: testing },
    ].filter(section => section.items.length > 0);
  }, [filteredAndSortedChatbots]);

  const toggleSection = (key: string) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSort = (field: 'name' | 'date') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <div>
      <AppHeader actions={<HeaderUtilityActions />}>
        <div className="header-brand">
          <img src="/logo.png" alt="Neural Intelligence" className="header-logo" />
          <h1>Neural Intelligence</h1>
        </div>
      </AppHeader>

      <div className="container">
        <div className="page-header flex flex-between flex-center">
          <div>
            <h2 className="page-title" style={{ fontSize: '32px', fontWeight: 700 }}>My Chatbots</h2>
            <p className="page-description" style={{ fontSize: '17px', marginTop: '8px' }}>View and manage all your chatbots in one place</p>
          </div>
          {user?.role === 'admin' && (
            <button onClick={() => setShowCreate(true)} className="btn btn-primary" style={{ fontSize: '16px', padding: '14px 24px', fontWeight: 600 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Create New Chatbot
            </button>
          )}
        </div>

        {showCreate && (
          <div className="card mb-4" style={{ background: 'var(--color-primary-light)', borderColor: 'var(--color-primary)' }}>
            <form onSubmit={handleCreate} className="flex gap-3 flex-center">
              <input
                type="text"
                className="input flex-1"
                placeholder="Give your chatbot a name (e.g., Customer Support Bot)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
                style={{ fontSize: '16px', padding: '12px 16px' }}
              />
              <button type="submit" className="btn btn-primary" disabled={creating} style={{ fontSize: '16px', padding: '12px 20px', fontWeight: 600 }}>
                {creating ? (
                  <>
                    <span className="spinner spinner-sm" />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setShowCreate(false);
                  setNewName('');
                }}
                style={{ fontSize: '16px', padding: '12px 20px' }}
              >
                Cancel
              </button>
            </form>
          </div>
        )}

        {!loading && chatbots.length > 0 && (
          <div className="card mb-4" style={{ padding: '16px 20px' }}>
            <div className="search-toolbar">
              <div className="flex-1" style={{ position: 'relative', minWidth: 0 }}>
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  style={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  className="input"
                  placeholder="Search chatbots by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ fontSize: '16px', padding: '12px 16px 12px 44px', width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '15px', color: 'var(--color-text-secondary)', fontWeight: 600 }}>Sort by:</span>
                <button
                  onClick={() => handleSort('name')}
                  className="btn btn-secondary"
                  style={{ 
                    fontSize: '15px', 
                    padding: '10px 16px',
                    fontWeight: 600,
                    background: sortBy === 'name' ? 'var(--color-primary)' : undefined,
                    color: sortBy === 'name' ? 'white' : undefined,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  Name
                  {sortBy === 'name' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {sortOrder === 'asc' ? (
                        <polyline points="18,15 12,9 6,15" />
                      ) : (
                        <polyline points="6,9 12,15 18,9" />
                      )}
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => handleSort('date')}
                  className="btn btn-secondary"
                  style={{ 
                    fontSize: '15px', 
                    padding: '10px 16px',
                    fontWeight: 600,
                    background: sortBy === 'date' ? 'var(--color-primary)' : undefined,
                    color: sortBy === 'date' ? 'white' : undefined,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  Date Created
                  {sortBy === 'date' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {sortOrder === 'asc' ? (
                        <polyline points="18,15 12,9 6,15" />
                      ) : (
                        <polyline points="6,9 12,15 18,9" />
                      )}
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="chatbot-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card">
                <div className="flex gap-3 mb-4">
                  <div className="skeleton" style={{ width: 44, height: 44 }} />
                  <div className="flex-1">
                    <div className="skeleton mb-2" style={{ height: 20, width: '60%' }} />
                    <div className="skeleton" style={{ height: 16, width: '40%' }} />
                  </div>
                </div>
                <div className="skeleton" style={{ height: 16, width: '30%' }} />
              </div>
            ))}
          </div>
        ) : chatbots.length === 0 ? (
          <div className="card empty-state">
            <div className="empty-state-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <h3 className="empty-state-title" style={{ fontSize: '24px', fontWeight: 700 }}>Welcome! Let's Create Your First Chatbot</h3>
            <p className="empty-state-description" style={{ fontSize: '17px', lineHeight: '1.6', maxWidth: '500px', margin: '0 auto 24px' }}>
              A chatbot helps answer your customers' questions automatically, 24/7. It's like having a helpful assistant on your website that never takes a break!
            </p>
            <button onClick={() => setShowCreate(true)} className="btn btn-primary" style={{ fontSize: '16px', padding: '14px 24px', fontWeight: 600 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Get Started - Create Your Chatbot
            </button>
          </div>
        ) : (filteredAndSortedChatbots.length === 0 && searchQuery) ? (
          <div className="card" style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <h3 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px', color: 'var(--color-text-secondary)' }}>No chatbots found</h3>
            <p style={{ fontSize: '16px', color: 'var(--color-text-secondary)' }}>Try adjusting your search terms</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {categorizedChatbots.map(section => {
              const isCollapsed = collapsedSections[section.key];
              return (
                <div key={section.key}>
                  <div
                    onClick={() => toggleSection(section.key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 16px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      borderBottom: '2px solid var(--color-border)',
                      marginBottom: isCollapsed ? 0 : '3px',
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      style={{
                        color: 'var(--color-text-muted)',
                        transition: 'transform 0.15s',
                        transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                      }}
                    >
                      <path d="M8 5l8 7-8 7z" />
                    </svg>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, margin: 0, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {section.label}
                    </h3>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: 600,
                      background: 'var(--color-bg-secondary)',
                      color: 'var(--color-text-secondary)',
                    }}>
                      {section.items.length}
                    </span>
                  </div>

                  {!isCollapsed && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {section.items.map(chatbot => (
                        <div
                          key={chatbot.id}
                          className="card"
                          onClick={() => navigate(
                            user?.role === 'admin' || (user?.role === 'sales' && chatbot.editable_by_sales)
                              ? `/chatbots/${chatbot.id}`
                              : `/chatbots/${chatbot.id}/preview`
                          )}
                          style={{
                            cursor: 'pointer',
                            padding: '12px 16px',
                            transition: 'all 0.2s ease',
                            border: '1px solid var(--color-border)',
                            marginBottom: 0
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'var(--color-primary)';
                            e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'var(--color-border)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '10px',
                              background: (() => {
                                if (!chatbot.theme_json) return 'var(--color-bg-secondary)';
                                try {
                                  const theme = JSON.parse(chatbot.theme_json);
                                  return theme.headerBgColor || 'var(--color-bg-secondary)';
                                } catch {
                                  return 'var(--color-bg-secondary)';
                                }
                              })(),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              overflow: 'hidden'
                            }}>
                              {chatbot.logo_url ? (
                                <img
                                  src={chatbot.logo_url}
                                  alt={chatbot.name}
                                  style={{ width: '28px', height: '28px', objectFit: 'contain' }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const svg = e.currentTarget.nextElementSibling;
                                    if (svg) {
                                      (svg as HTMLElement).style.display = 'block';
                                    }
                                  }}
                                />
                              ) : null}
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                style={{ display: chatbot.logo_url ? 'none' : 'block' }}
                              >
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <h3 style={{
                                  fontSize: '17px',
                                  fontWeight: 600,
                                  margin: 0,
                                  color: 'var(--color-text-primary)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {chatbot.name}
                                </h3>
                                {Boolean(chatbot.editable_by_sales) && (
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '3px 8px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    background: user?.role === 'admin' ? 'var(--color-role-admin-bg)' : 'var(--color-role-customer-bg)',
                                    color: user?.role === 'admin' ? 'var(--color-role-admin-text)' : 'var(--color-role-customer-text)',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                      <circle cx="9" cy="7" r="4" />
                                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                    {user?.role === 'admin' ? 'Sales Editable' : 'You can edit'}
                                  </span>
                                )}
                                {Boolean(chatbot.has_customer) && (
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '3px 8px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    background: 'var(--color-badge-customer-assigned-bg)',
                                    color: 'var(--color-badge-customer-assigned-text)',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                      <circle cx="12" cy="7" r="4" />
                                    </svg>
                                    Customer Assigned
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '15px', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                  <line x1="16" y1="2" x2="16" y2="6" />
                                  <line x1="8" y1="2" x2="8" y2="6" />
                                  <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                                Created {new Date(chatbot.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    user?.role === 'admin' || (user?.role === 'sales' && chatbot.editable_by_sales)
                                      ? `/chatbots/${chatbot.id}`
                                      : `/chatbots/${chatbot.id}/preview`
                                  );
                                }}
                                className="btn btn-secondary"
                                style={{ fontSize: '15px', padding: '10px 20px', fontWeight: 600 }}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                                  {(user?.role === 'sales' && !chatbot.editable_by_sales) ? (
                                    <>
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                      <circle cx="12" cy="12" r="3" />
                                    </>
                                  ) : (
                                    <>
                                      <circle cx="12" cy="12" r="3" />
                                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                                    </>
                                  )}
                                </svg>
                                {(user?.role === 'admin' || (user?.role === 'sales' && chatbot.editable_by_sales)) ? 'Edit' : 'Preview'}
                              </button>
                              {user?.role === 'admin' && (
                                <button
                                  onClick={(e) => handleDelete(e, chatbot.id)}
                                  className="btn btn-ghost btn-icon"
                                  title="Delete chatbot"
                                  style={{ padding: '10px' }}
                                  disabled={deletingId === chatbot.id}
                                >
                                  {deletingId === chatbot.id ? (
                                    <span className="spinner spinner-sm" />
                                  ) : (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="3,6 5,6 21,6" />
                                      <path d="M19,6v14a2,2 0 0 1-2,2H7a2,2 0 0 1-2-2V6m3,0V4a2,2 0 0 1,2-2h4a2,2 0 0 1,2,2v2" />
                                    </svg>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
